import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import RulerHorizontalIcon from '../icons/RulerHorizontalIcon';
import HandIcon from '../icons/HandIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import Player from '../types/Player';
import Position from '../types/Position';
import HighSchool from '../types/HighSchool';
import HighSchoolCombine from '../types/HighSchoolCombine';
import { inchesToFeetAndInches } from '../services/converter';
import { getMostRecentDataByProperty } from '../services/player';
import CircleBadge from '../atoms/CircleBadge';
import { COLOR_BLACK } from '../styles/colors';

interface HighSchoolBiometricsProps {
  className?: string;
  player?: Player;
  primarySchool?: HighSchool;
  selectedPosition?: Position;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  badgeWrap: {
    marginRight: theme.spacing(2),
    width: 45,
  },
  badgeVal: {
    color: COLOR_BLACK,
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    paddingRight: 0,
    marginLeft: 'auto',
    cursor: 'help',
  },
}), { name: 'HighSchoolBiometrics' });

export default function HighSchoolBiometrics(props: HighSchoolBiometricsProps) {
  const { className, player, selectedPosition } = props;
  const classes = useStyles();

  const primarySchool = ((player && player.highSchools) || [])
    .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);
  
  const heightPercentile: any = primarySchool && primarySchool.heightPerc && (primarySchool.heightPerc
    .find((heightPerc: any) => heightPerc && heightPerc.code === selectedPosition) || {} as any).percentile;
  const weightPercentile: any = primarySchool && primarySchool.weightPerc && (primarySchool.weightPerc
    .find((weightPerc: any) => weightPerc && weightPerc.code === selectedPosition) || {} as any).percentile;

  const highSchoolCombines: HighSchoolCombine[] = (player && player.hsCombines) || [];
  const wingspan = getMostRecentDataByProperty(highSchoolCombines, 'wingspan');
  const wingspanPerc = getMostRecentDataByProperty(highSchoolCombines, 'wingspanPerc');
  const arm = getMostRecentDataByProperty(highSchoolCombines, 'arm');
  const armPerc = getMostRecentDataByProperty(highSchoolCombines, 'armPerc');
  const hand = getMostRecentDataByProperty(highSchoolCombines, 'hand');
  const handPerc = getMostRecentDataByProperty(highSchoolCombines, 'handPerc');
  

  const data = {
    playerId: player?.id,
    height: primarySchool?.height,
    heightPercentile: heightPercentile,
    weight: primarySchool?.weight,
    weightPercentile: weightPercentile,
    wingspan: wingspan,
    wingspanPercentile: wingspan ? wingspanPerc?.find((wp: any) => wp.code === selectedPosition)?.percentile : undefined,
    arm: arm,
    armPercentile: arm ? armPerc?.find((ap: any) => ap.code === selectedPosition)?.percentile : undefined,
    hand: hand,
    handPercentile: hand ? handPerc?.find((hp: any) => hp.code === selectedPosition)?.percentile : undefined,
    overallHSBiometricRating: 0,
  }

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      name={
        <div className={classes.header}>
          <CircleBadge
            className={{ badgeWrap: classes.badgeWrap, badgeVal: classes.badgeVal }}
            value="BIO"
            height={45}
            width={45}
          />
          <span>High School Biometrics</span>
        </div>
      }
    >
      {/* Height */}
      {data.height && (
        <ListItem
          icon={RulerVerticalIcon}
          title="Height:"
        >
          <b>{inchesToFeetAndInches(data.height)}</b>

          {(typeof data.heightPercentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  value={data.heightPercentile}
                />
              </div>
            </Tooltip>
          )}
        </ListItem>
      )}

      {/* Weight */}
      {data.weight && (
        <ListItem
          icon={ScaleIcon}
          title="Weight:"
        >
          <b>{data.weight} lbs.</b>

          {(typeof data.weightPercentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  value={data.weightPercentile}
                />
              </div>
            </Tooltip>
          )}
        </ListItem>
      )}

      {/* Wingspan */}
      {data.wingspan && (
        <ListItem
          icon={RulerHorizontalIcon}
          title="Wingspan:"
        >
          <b>{data.wingspan}</b>
          {(typeof data.wingspanPercentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  value={data.wingspanPercentile}
                />
              </div>
            </Tooltip>
          )}
        </ListItem>
      )}

      {/* Arm */}
      {data.arm && (
        <ListItem
          icon={RulerHorizontalIcon}
          title="Arm:"
        >
          <b>{data.arm}</b>
          {(typeof data.armPercentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  value={data.armPercentile}
                />
              </div>
            </Tooltip>
          )}
        </ListItem>
      )}

      {/* Hand */}
      {data.hand && (
        <ListItem
          icon={HandIcon}
          title="Hand:"
        >
          <b>{data.hand}</b>
          {(typeof data.handPercentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  value={data.handPercentile}
                />
              </div>
            </Tooltip>
          )}
        </ListItem>
      )}

      {/* Overall Rating */}
      {data?.overallHSBiometricRating ? (
        <ListItem
          icon={RulerVerticalIcon}
          title="Overall HS Biometric Rating:"
        >
          <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
            <div className={classes.progressLineWrapper}>
              <ProgressLine
                className={classes.progressLine}
                value={data.overallHSBiometricRating}
              />
            </div>
          </Tooltip>
        </ListItem>
      ): ''}
    </NamedBox>
  );
}

